/**
 * useModalZIndex Hook
 *
 * Provides automatic z-index management for stacked Mantine modals.
 *
 * Problem: When modal A opens modal B (e.g., GreetingCandidatePicker → UpgradeModal),
 * all modals render at the same default z-index (200), causing the newer modal to
 * appear behind the older one.
 *
 * Solution: A global counter that assigns each newly opened modal a higher z-index
 * than all currently visible modals. When all modals close, the counter resets to
 * prevent unbounded growth during long sessions.
 *
 * Why not Modal.Stack? Mantine's Modal.Stack requires all potentially-stacking modals
 * to be declared together under a single wrapper with pre-registered IDs. Our modals
 * are distributed across independent components (GreetingCandidatePicker, UpgradeModal,
 * ScheduleCardModal, etc.), making centralization impractical without breaking
 * component encapsulation.
 *
 * Usage:
 *   const zIndex = useModalZIndex(opened);
 *   <Modal opened={opened} onClose={close} zIndex={zIndex}>
 */

import { useState, useEffect, useRef } from 'react';

/** Mantine's default modal z-index */
export const MODAL_Z_INDEX_BASE = 200;

/** Gap between stacked modals — large enough to avoid collisions with overlay */
export const MODAL_Z_INDEX_STEP = 10;

/** Confirm dialogs that must sit above a parent wizard shell (#1455). */
export function stackedModalZIndexAbove(parentZIndex: number): number {
  return parentZIndex + MODAL_Z_INDEX_STEP;
}

/** Tracks the next z-index to assign to an opening modal */
let nextZIndex = MODAL_Z_INDEX_BASE;

/** How many modals are currently open (for reset detection) */
let openCount = 0;

/**
 * Returns a z-index that auto-increments globally each time a modal opens.
 * Guarantees any newly opened modal stacks above all currently visible ones.
 *
 * The z-index is assigned via useEffect (one render after opened becomes true).
 * This is safe because Mantine's Modal transition starts from opacity 0, so
 * the brief first frame at the base z-index is invisible to the user.
 *
 * @param opened - The modal's current opened state
 * @returns A z-index value to pass to the Modal's zIndex prop
 */
export function useModalZIndex(opened: boolean): number {
  const [zIndex, setZIndex] = useState(MODAL_Z_INDEX_BASE);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (opened && !wasOpenRef.current) {
      openCount++;
      nextZIndex += MODAL_Z_INDEX_STEP;
      setZIndex(nextZIndex);
      wasOpenRef.current = true;
    } else if (!opened && wasOpenRef.current) {
      openCount = Math.max(0, openCount - 1);
      if (openCount === 0) {
        nextZIndex = MODAL_Z_INDEX_BASE;
      }
      wasOpenRef.current = false;
    }
  }, [opened]);

  // Decrement on unmount if the modal was still open (e.g., parent component unmounts)
  useEffect(() => {
    return () => {
      if (wasOpenRef.current) {
        openCount = Math.max(0, openCount - 1);
        if (openCount === 0) {
          nextZIndex = MODAL_Z_INDEX_BASE;
        }
        wasOpenRef.current = false;
      }
    };
  }, []);

  return zIndex;
}
